import { getAuthToken } from "@/lib/auth/get-auth-token";
import { getApiBaseUrl, getApiKey } from "@/lib/config/env";

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  params?: Record<
    string,
    | string
    | number
    | boolean
    | undefined
    | null
    | (string | number)[]
  >;
};

export class ApiService {
  protected readonly baseUrl: string;

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl ?? getApiBaseUrl();
  }

  protected getAuthHeaders(): HeadersInit {
    const token = getAuthToken();

    if (!token) {
      return {};
    }

    return {
      Authorization: `Bearer ${token}`,
    };
  }

  protected buildUrl(
    path: string,
    params?: RequestOptions["params"],
  ): string {
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    const url = new URL(`${this.baseUrl}${normalizedPath}`);

    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null) {
          continue;
        }

        if (Array.isArray(value)) {
          const arrayKey = key.endsWith("[]") ? key : `${key}[]`;
          for (const item of value) {
            url.searchParams.append(arrayKey, String(item));
          }
          continue;
        }

        url.searchParams.set(key, String(value));
      }
    }

    return url.toString();
  }

  protected async request<T>(
    path: string,
    options: RequestOptions = {},
  ): Promise<T> {
    const { body, params, headers, ...rest } = options;
    const url = this.buildUrl(path, params);

    const response = await fetch(url, {
      ...rest,
      headers: {
        Accept: "application/json",
        "X-API-KEY": getApiKey(),
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...this.getAuthHeaders(),
        ...headers,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    const contentType = response.headers.get("content-type");
    const hasJsonBody = contentType?.includes("application/json");
    const responseBody = hasJsonBody ? await response.json() : await response.text();

    if (!response.ok) {
      const message =
        typeof responseBody === "object" &&
        responseBody !== null &&
        "message" in responseBody &&
        typeof responseBody.message === "string"
          ? responseBody.message
          : `Request failed with status ${response.status}`;

      throw new ApiError(message, response.status, responseBody);
    }

    return responseBody as T;
  }

  protected get<T>(path: string, options?: Omit<RequestOptions, "body">) {
    return this.request<T>(path, { ...options, method: "GET" });
  }

  protected post<T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, "body">,
  ) {
    return this.request<T>(path, { ...options, method: "POST", body });
  }

  protected put<T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, "body">,
  ) {
    return this.request<T>(path, { ...options, method: "PUT", body });
  }

  protected patch<T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions, "body">,
  ) {
    return this.request<T>(path, { ...options, method: "PATCH", body });
  }

  protected delete<T>(path: string, options?: Omit<RequestOptions, "body">) {
    return this.request<T>(path, { ...options, method: "DELETE" });
  }
}
